import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Heading from 'LuxKit/Typography/Heading'
import BodyText, { BodyTextLarge } from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import TextLink from 'LuxKit/TextLink'
import MessagingLabel from 'LuxKit/Label/MessagingLabel'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'
import ClassicRewardsSealIcon from 'LuxKit/Icons/loyalty/ClassicRewardsSealIcon'
import { useAppSelector } from 'hooks/reduxHooks'
import PromoMembershipMessage from './PromoMembershipMessage'
import { promoMessages, usePromoValues } from './promoContent'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(20)};
  ${mediaQueryUp.desktop} { gap: ${rem(8)}; }
`
const Panel = styled.div`
  overflow: hidden;
  border: ${rem(1)} solid ${props => props.theme.palette.messaging.urgency.normalForeground};
  border-radius: ${rem(12)};
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  padding-block: ${rem(24)};
  padding-inline: ${rem(28)};
  background: ${props => props.theme.palette.neutral.surface.lightBackground};
  ${mediaQueryUp.desktop} {
    flex-direction: row;
    align-items: center;
    gap: ${rem(24)};
  }
`
const Labels = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(10)};
`
const Copy = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${rem(12)};
`
const Description = styled(BodyText)`
  ${mediaQueryUp.desktop} {
    && { ${BodyTextLarge} }
  }
`
const Actions = styled.div`
  display: flex;
  gap: ${rem(8)};
  > * { flex: 1; }
  ${mediaQueryUp.desktop} {
    flex-direction: row-reverse;
    flex-shrink: 0;
    > * { flex: initial; }
  }
`
const Footer = styled.div`
  padding-block: ${rem(12)};
  padding-inline: ${rem(20)};
  border-block-start: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`
const fireIcon = <SolidFireIcon />

interface Props {
  onLearnMore: () => void
  onViewOffers: () => void
}

export default function MaldivesOnePointPromoBanner({ onLearnMore, onViewOffers }: Props) {
  const intl = useIntl()
  const values = usePromoValues()
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const points = useAppSelector(state => state.loyalty.account.data?.points)
  const isLoggedIn = !!memberId
  const showFooter = !isLoggedIn || (points ?? 0) < 1

  return <Wrapper data-testid="maldives-one-point-promo">
    <Panel>
      <Content>
        <Copy>
          <Labels>
            <MessagingLabel variant="urgency" startIcon={fireIcon}>
              {intl.formatMessage({
                id: 'JAUodODORoJDus7m1T2F',
                defaultMessage: 'Flight sale',
                description: 'Urgency badge above the headline of the Maldives one-point flight sale banner.',
              })}
            </MessagingLabel>
            <Group direction="horizontal" gap={4} verticalAlign="center">
              <ClassicRewardsSealIcon size={16} colour="highlight-primary-dark" />
              <BodyText variant="medium" weight="bold" colour="highlight-primary-dark">
                {intl.formatMessage({
                  id: 'Ye5UJh242YEaWKy7ywpT',
                  defaultMessage: 'Société Rewards',
                  description: 'Rewards programme label beside the flight sale badge in the Maldives one-point promotion.',
                })}
              </BodyText>
            </Group>
          </Labels>
          <Group direction="vertical" gap={4}>
            <Heading variant="heading3" as="h2">{intl.formatMessage(promoMessages.title, values)}</Heading>
            <Description variant="medium">
              {intl.formatMessage({
                id: 'YzveD9_AEyGjSfyqAJDu',
                defaultMessage: 'To celebrate the launch of Société Rewards Seats, book an eligible hotel stay and fly from Melbourne to Malé on {date} with this limited time offer.',
                description: 'Description of the Maldives one-point sale explaining the eligible hotel stay and charter departure date.',
              }, values)}
            </Description>
          </Group>
        </Copy>
        <Actions>
          <TextButton kind="primary" size="medium" shape="round" onClick={onViewOffers}>
            {intl.formatMessage({
              id: 'lLdDWv-1fXU-96GQldF6',
              defaultMessage: 'View offers',
              description: 'Button applying the one-point Maldives promotion dates and reward fare filters to the hotel offers.',
            })}
          </TextButton>
          <TextButton kind="secondary" size="medium" shape="round" onClick={onLearnMore}>
            {intl.formatMessage({
              id: 'bP1n_cSCH9eiOrMKLZUU',
              defaultMessage: 'Learn more',
              description: 'Button opening the information modal for the Maldives one-point charter flight promotion.',
            })}
          </TextButton>
        </Actions>
      </Content>
      {showFooter && <Footer><BodyText variant="medium"><PromoMembershipMessage isLoggedIn={isLoggedIn} /></BodyText></Footer>}
    </Panel>
    <BodyText variant="small"><TextLink to="/promotions" size="small" weight="regular">{intl.formatMessage(promoMessages.terms)}</TextLink></BodyText>
  </Wrapper>
}
